import React, { useEffect, useState } from 'react';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, ArrowRight } from 'lucide-react';
import { useForm, ValidationError } from '@formspree/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Formspree hook (uses your form ID)
  const [state, handleSubmit] = useForm('xpqdwdra');

  useEffect(() => {
    if (state.succeeded) {
      toast.success("Message sent. We'll get back to you within 24 hours.");
      setFormData({ name: '', email: '', phone: '', message: '' });
    }
  }, [state.succeeded]);

  useEffect(() => {
    if (state.errors && state.errors.length > 0) {
      toast.error('Could not send your message. Please double-check the form and try again.');
    }
  }, [state.errors]);

  const handleChange = (e) => {
    setFormData((prev) => {
      return {
        ...prev,
        [e.target.name]: e.target.value
      };
    });
  };

  return (
    <div className="bg-white pt-16 lg:pt-20">
      {/* Hero Section - Dark */}
      <section className="relative py-20 lg:py-28 bg-zinc-900 overflow-hidden">
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('/images/Decking/1.0.jpg')`,
          }}
        />

        {/* Orange accent */}
        <div className="absolute left-0 top-0 bottom-0 w-1 lg:w-2 bg-orange-500" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-0.5 bg-orange-500" />
              <span className="text-orange-500 text-xs font-bold uppercase tracking-widest">Get In Touch</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-[0.9] mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-zinc-300 leading-relaxed">
              Ready to start? Have questions? Reach out — we respond fast.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section - Light */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-5">
              <h2 className="text-3xl font-black text-zinc-900 uppercase tracking-tight mb-6">
                Let's Talk
              </h2>
              <p className="text-zinc-600 mb-10 leading-relaxed">
                Whether you've got a project ready to go or just want to bounce around some ideas — give us a call or fill out the form. Free estimates, no pressure.
              </p>

              <div className="space-y-4">
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center gap-4 p-4 bg-zinc-50 border border-zinc-200 hover:border-orange-300 hover:shadow-md transition-all group"
                >
                  <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-black text-zinc-900 uppercase text-sm">Phone</p>
                    <p className="text-zinc-600 group-hover:text-orange-500 transition-colors">{companyInfo.phone}</p>
                  </div>
                </a>

                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-4 p-4 bg-zinc-50 border border-zinc-200 hover:border-orange-300 hover:shadow-md transition-all group"
                >
                  <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-black text-zinc-900 uppercase text-sm">Email</p>
                    <p className="text-zinc-600 group-hover:text-orange-500 transition-colors">{companyInfo.email}</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 bg-zinc-50 border border-zinc-200">
                  <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-black text-zinc-900 uppercase text-sm">Service Area</p>
                    <p className="text-zinc-600">Halifax Metro Area & Surrounding Communities</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-zinc-50 border border-zinc-200">
                  <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-black text-zinc-900 uppercase text-sm">Hours</p>
                    <p className="text-zinc-600">Mon-Fri: 8:30am - 5pm</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-10 pt-10 border-t border-zinc-200">
                <p className="font-black text-zinc-900 uppercase text-sm mb-4">Follow Our Work</p>
                <div className="flex gap-3">
                  <a
                    href={companyInfo.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-zinc-100 border border-zinc-200 flex items-center justify-center hover:bg-orange-500 hover:border-orange-500 text-zinc-600 hover:text-white transition-colors"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a
                    href={companyInfo.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-zinc-100 border border-zinc-200 flex items-center justify-center hover:bg-orange-500 hover:border-orange-500 text-zinc-600 hover:text-white transition-colors"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-7">
              <div className="bg-zinc-50 border border-zinc-200 p-8 lg:p-10">
                <h3 className="text-2xl font-black text-zinc-900 uppercase tracking-tight mb-2">Get a Free Quote</h3>
                <p className="text-zinc-500 mb-8">Fill out the form. We'll get back to you within 24 hours.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Optional: helps label submissions in inbox */}
                  <input type="hidden" name="subject" value="New website inquiry - House of Renovation & Design" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-zinc-900 font-bold uppercase text-xs tracking-wide">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="mt-2 h-12 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-orange-500 focus:ring-orange-500 rounded-none"
                      />
                      <ValidationError prefix="Name" field="name" errors={state.errors} />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-zinc-900 font-bold uppercase text-xs tracking-wide">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(902) 555-0123"
                        className="mt-2 h-12 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-orange-500 focus:ring-orange-500 rounded-none"
                      />
                      <ValidationError prefix="Phone" field="phone" errors={state.errors} />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-zinc-900 font-bold uppercase text-xs tracking-wide">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="mt-2 h-12 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-orange-500 focus:ring-orange-500 rounded-none"
                    />
                    <ValidationError prefix="Email" field="email" errors={state.errors} />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-zinc-900 font-bold uppercase text-xs tracking-wide">Project Details *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project — what needs to be done, timeline, budget, etc."
                      rows={6}
                      className="mt-2 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-orange-500 focus:ring-orange-500 resize-none rounded-none"
                    />
                    <ValidationError prefix="Message" field="message" errors={state.errors} />
                  </div>

                  <Button
                    type="submit"
                    disabled={state.submitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-black uppercase tracking-wide h-14 text-base rounded-none"
                  >
                    {state.submitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Send Message
                        <ArrowRight className="w-5 h-5" />
                      </span>
                    )}
                  </Button>

                  {/* Optional small success text under button (toast already shows) */}
                  {state.succeeded && (
                    <p className="text-sm text-green-700 font-semibold">
                      Thanks — your message was sent successfully.
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-96 border-t border-zinc-200">
        <iframe
          title="Halifax Map"
          src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d152697.6462973708!2d-63.62097029427679!3d44.67203640753733!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sca!4v1769312531388!5m2!1sen!2sca"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>
    </div>
  );
};

export default ContactPage;
